/**
 * PretextDemo - Demo page showcasing the Pretext-powered chart
 * Fetches real contest data and live price for demonstration
 */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { PretextChart, ContestEntry } from '../components/PretextChart';
import { Theme, darkTheme, lightTheme } from '../theme';
import axios from 'axios';
import priceWebSocket from '../services/priceWebSocket';

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  color: #fff;
  padding: 20px;
`;

const Header = styled.header`
  text-align: center;
  padding: 40px 20px;
  border-bottom: 1px solid rgba(247, 147, 26, 0.3);
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  background: linear-gradient(90deg, #f7931a, #ffb347, #f7931a);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 3s linear infinite;
  margin-bottom: 10px;
  
  @keyframes shimmer {
    0% { background-position: 0% center; }
    100% { background-position: 200% center; }
  }
`;

const Subtitle = styled.p`
  color: #888;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const ChartSection = styled.div`
  display: flex;
  gap: 20px;
  max-width: 1600px;
  margin: 0 auto;
  height: 650px;
`;

const ChartWrapper = styled.div`
  flex: 1;
  min-width: 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const CardPanel = styled.div`
  width: 280px;
  background: rgba(10, 10, 20, 0.9);
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1100px) {
    display: none;
  }
`;

const PanelHeader = styled.div`
  padding: 16px;
  background: rgba(247, 147, 26, 0.1);
  border-bottom: 1px solid rgba(247, 147, 26, 0.2);
  
  h3 {
    margin: 0;
    color: #f7931a;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .count {
    color: #888;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const PanelEntryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(247, 147, 26, 0.3);
    border-radius: 3px;
  }
`;

const PanelEntry = styled.div<{ $isSelected: boolean; $isBullish: boolean; $isLeader: boolean }>`
  padding: 10px 12px;
  margin-bottom: 6px;
  background: ${props => props.$isSelected 
    ? 'rgba(247, 147, 26, 0.2)' 
    : props.$isLeader 
      ? 'rgba(255, 215, 0, 0.1)'
      : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.$isSelected 
    ? '#f7931a' 
    : props.$isLeader 
      ? 'rgba(255, 215, 0, 0.4)'
      : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(247, 147, 26, 0.15);
    border-color: rgba(247, 147, 26, 0.5);
  }
  
  .handle {
    color: #f7931a;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .price {
    font-size: 15px;
    font-weight: 700;
    color: ${props => props.$isLeader 
      ? '#FFD700' 
      : props.$isBullish 
        ? '#d4740f' 
        : '#8b5a00'};
  }
  
  .distance {
    font-size: 10px;
    color: #666;
    margin-top: 2px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  max-width: 1000px;
  margin: 40px auto;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(247, 147, 26, 0.2);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(247, 147, 26, 0.1);
    border-color: rgba(247, 147, 26, 0.5);
    transform: translateY(-4px);
  }
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #f7931a;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InfoSection = styled.section`
  max-width: 900px;
  margin: 60px auto;
  padding: 40px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  border: 1px solid rgba(247, 147, 26, 0.1);
`;

const InfoTitle = styled.h2`
  color: #f7931a;
  margin-bottom: 20px;
  font-size: 1.5rem;
`;

const InfoText = styled.p`
  color: #aaa;
  line-height: 1.8;
  margin-bottom: 16px;
`;

const CodeBlock = styled.pre`
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
  font-family: 'Fira Code', 'Monaco', monospace;
  font-size: 0.9rem;
  color: #0f0;
  margin: 20px 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 20px;
`;

const FeatureItem = styled.li`
  background: rgba(247, 147, 26, 0.05);
  border-left: 3px solid #f7931a;
  padding: 16px;
  border-radius: 0 8px 8px 0;
  
  &::before {
    content: '✓ ';
    color: #00d395;
    font-weight: bold;
  }
`;

const LivePriceDisplay = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #00d395;
  border-radius: 8px;
  padding: 12px 20px;
  z-index: 1000;
  
  .label {
    font-size: 10px;
    color: #00d395;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .price {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
  }
  
  .indicator {
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #00d395;
    border-radius: 50%;
    margin-right: 6px;
    animation: pulse 1s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const EntryList = styled.div`
  max-width: 1200px;
  margin: 40px auto;
`;

const EntryListTitle = styled.h2`
  color: #f7931a;
  margin-bottom: 20px;
  text-align: center;
`;

const EntryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const EntryCard = styled.div<{ $isBullish: boolean; $isLeader: boolean }>`
  background: ${props => props.$isLeader 
    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.1))'
    : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.$isLeader 
    ? '#FFD700' 
    : props.$isBullish 
      ? 'rgba(247, 147, 26, 0.5)' 
      : 'rgba(139, 90, 0, 0.4)'};
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 20px rgba(247, 147, 26, 0.2);
  }
  
  .handle {
    color: #f7931a;
    font-size: 0.85rem;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .price {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${props => props.$isLeader 
      ? '#FFD700' 
      : props.$isBullish 
        ? '#d4740f' 
        : '#8b5a00'};
  }
  }
  
  .crown {
    position: absolute;
    top: -8px;
    right: -8px;
    font-size: 1.2rem;
  }
`;

// ============================================================================
// Main Component
// ============================================================================

const PretextDemo: React.FC = () => {
  const [entries, setEntries] = useState<ContestEntry[]>([]);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [priceData, setPriceData] = useState<{ date: Date; close: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<ContestEntry | null>(null);
  
  const theme = darkTheme;
  
  // Calculate leader
  const leader = entries.length > 0 && currentPrice > 0
    ? entries.reduce((closest, entry) => {
        const closestDist = Math.abs(closest.price_prediction - currentPrice);
        const entryDist = Math.abs(entry.price_prediction - currentPrice);
        return entryDist < closestDist ? entry : closest;
      }, entries[0])
    : null;
  
  // Fetch contest entries
  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const response = await axios.get('/api/contest/bitcoin2026/entries');
        if (response.data.success) {
          const formatted = response.data.entries.map((e: any) => ({
            ...e,
            price_prediction: parseFloat(e.price_prediction)
          }));
          setEntries(formatted);
        }
      } catch (err) {
        console.error('Failed to fetch entries:', err);
        setError('Failed to load contest entries');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEntries();
  }, []);
  
  // Fetch historical price data for the chart
  useEffect(() => {
    const fetchPriceData = async () => {
      try {
        // Fetch last 90 days of price data
        const response = await axios.get('/api/prices/historical?days=90');
        if (response.data && Array.isArray(response.data)) {
          const formatted = response.data.map((p: any) => ({
            date: new Date(p.timestamp || p.date),
            close: parseFloat(p.close || p.price)
          })).filter((p: any) => !isNaN(p.close) && p.date instanceof Date);
          setPriceData(formatted);
        }
      } catch (err) {
        console.log('Could not fetch historical prices:', err);
      }
    };
    
    fetchPriceData();
  }, []);
  
  // Subscribe to live price
  useEffect(() => {
    const handlePriceUpdate = (data: { price: number }) => {
      setCurrentPrice(data.price);
    };
    
    priceWebSocket.connect();
    priceWebSocket.on('priceUpdate', handlePriceUpdate);
    
    // Also fetch initial price
    const fetchInitialPrice = async () => {
      try {
        const response = await axios.get('/api/prices/current');
        if (response.data.price) {
          setCurrentPrice(response.data.price);
        }
      } catch (err) {
        console.log('Using WebSocket for price');
      }
    };
    fetchInitialPrice();
    
    return () => {
      priceWebSocket.off('priceUpdate', handlePriceUpdate);
    };
  }, []);
  
  // Calculate stats
  const stats = {
    totalEntries: entries.length,
    avgPrediction: entries.length > 0 
      ? entries.reduce((sum, e) => sum + e.price_prediction, 0) / entries.length 
      : 0,
    minPrice: entries.length > 0 ? Math.min(...entries.map(e => e.price_prediction)) : 0,
    maxPrice: entries.length > 0 ? Math.max(...entries.map(e => e.price_prediction)) : 0,
    bullishCount: entries.filter(e => e.price_prediction > currentPrice).length,
    bearishCount: entries.filter(e => e.price_prediction <= currentPrice).length
  };
  
  const handleEntryClick = useCallback((entry: ContestEntry) => {
    setSelectedEntry(entry);
    // Just select the entry - don't open Twitter
    console.log('Selected entry:', entry.twitter_handle, '$' + entry.price_prediction.toLocaleString());
  }, []);
  
  if (loading) {
    return (
      <PageContainer>
        <Header>
          <Title>Loading...</Title>
        </Header>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      {/* Live Price Display */}
      <LivePriceDisplay>
        <div className="label"><span className="indicator" />Live BTC Price</div>
        <div className="price">${currentPrice.toLocaleString()}</div>
      </LivePriceDisplay>
      
      <Header>
        <Title>₿ Pretext Chart Demo</Title>
        <Subtitle>
          High-performance Canvas rendering with pure-JS text measurement.
          No DOM reflow. Smooth 60fps animations. Real contest data.
        </Subtitle>
      </Header>
      
      {/* Main Chart with Side Panel */}
      <ChartSection>
        <ChartWrapper>
          <PretextChart
            contestEntries={entries}
            priceData={priceData}
            currentPrice={currentPrice}
            theme={theme}
            onEntryClick={handleEntryClick}
          />
        </ChartWrapper>
        
        <CardPanel>
          <PanelHeader>
            <h3>📊 Predictions</h3>
            <div className="count">{entries.length} entries • sorted by distance</div>
          </PanelHeader>
          <PanelEntryList>
            {entries
              .sort((a, b) => Math.abs(a.price_prediction - currentPrice) - Math.abs(b.price_prediction - currentPrice))
              .map(entry => {
                const distance = entry.price_prediction - currentPrice;
                const distancePercent = currentPrice > 0 ? ((distance / currentPrice) * 100).toFixed(1) : '0';
                return (
                  <PanelEntry
                    key={entry.id}
                    $isSelected={selectedEntry?.id === entry.id}
                    $isBullish={entry.price_prediction > currentPrice}
                    $isLeader={entry.id === leader?.id}
                    onClick={() => handleEntryClick(entry)}
                  >
                    <div className="handle">
                      {entry.id === leader?.id && '👑 '}
                      @{entry.twitter_handle}
                    </div>
                    <div className="price">${entry.price_prediction.toLocaleString()}</div>
                    <div className="distance">
                      {distance >= 0 ? '+' : ''}{distancePercent}% from current
                    </div>
                  </PanelEntry>
                );
              })}
          </PanelEntryList>
        </CardPanel>
      </ChartSection>
      
      {/* Stats Grid */}
      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalEntries}</StatValue>
          <StatLabel>Total Entries</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>${Math.round(stats.avgPrediction).toLocaleString()}</StatValue>
          <StatLabel>Average Prediction</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#d4740f' }}>{stats.bullishCount}</StatValue>
          <StatLabel>Bullish (Above Current)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#8b5a00' }}>{stats.bearishCount}</StatValue>
          <StatLabel>Bearish (Below Current)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>${Math.round(stats.minPrice).toLocaleString()}</StatValue>
          <StatLabel>Lowest Prediction</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>${Math.round(stats.maxPrice).toLocaleString()}</StatValue>
          <StatLabel>Highest Prediction</StatLabel>
        </StatCard>
      </StatsGrid>
      
      {/* Info Section */}
      <InfoSection>
        <InfoTitle>Why Pretext?</InfoTitle>
        <InfoText>
          Traditional web text rendering requires DOM measurements like <code>getBoundingClientRect()</code> 
          or <code>offsetHeight</code>, which trigger expensive layout reflows. Pretext eliminates this 
          by implementing pure-JS text measurement using the browser's canvas font engine.
        </InfoText>
        
        <CodeBlock>{`// Traditional (slow, causes reflow)
const div = document.createElement('div');
div.textContent = text;
document.body.appendChild(div);
const height = div.offsetHeight; // REFLOW!
document.body.removeChild(div);

// Pretext (fast, no reflow)
const prepared = prepare(text, '16px Inter');
const { height } = layout(prepared, maxWidth, lineHeight);
// Pure arithmetic - no DOM touched!`}</CodeBlock>
        
        <FeatureList>
          <FeatureItem>60fps Canvas rendering</FeatureItem>
          <FeatureItem>No DOM layout reflow</FeatureItem>
          <FeatureItem>Cached text measurements</FeatureItem>
          <FeatureItem>Smooth starburst animations</FeatureItem>
          <FeatureItem>Real-time WebSocket prices</FeatureItem>
          <FeatureItem>92+ contest entries</FeatureItem>
          <FeatureItem>Golden angle spiral layout</FeatureItem>
          <FeatureItem>Interactive hover states</FeatureItem>
        </FeatureList>
      </InfoSection>
      
      {/* Entry List */}
      <EntryList>
        <EntryListTitle>All Contest Entries ({entries.length})</EntryListTitle>
        <EntryGrid>
          {entries
            .sort((a, b) => Math.abs(a.price_prediction - currentPrice) - Math.abs(b.price_prediction - currentPrice))
            .map(entry => (
              <EntryCard 
                key={entry.id}
                $isBullish={entry.price_prediction > currentPrice}
                $isLeader={entry.id === leader?.id}
                onClick={() => handleEntryClick(entry)}
              >
                {entry.id === leader?.id && <span className="crown">👑</span>}
                <div className="handle">@{entry.twitter_handle}</div>
                <div className="price">${entry.price_prediction.toLocaleString()}</div>
              </EntryCard>
            ))
          }
        </EntryGrid>
      </EntryList>
    </PageContainer>
  );
};

export default PretextDemo;
